import { FC, useEffect } from 'react';
import { EventCard } from 'components/Cards';
import { useRouter } from 'next/router';
import { RectangleSkeleton, Paging, Grid } from '@morressier/ts';
import HomeSearchNoResults from 'components/Search/HomeSearchNoResults';
import { Container, PagingContainer, CardLink } from '../style';
import { useEvents } from 'clients/internal/homeSearch';
import pagingLink from '../PagingLink';
import MetaTags from 'components/MetaTags';
import { logEvent } from 'utils/eventTracking';
import { ResultsText } from '../ResultsText';

interface ISearchEventsProps {
  inputTerm: string;
}

export const SkeletonLoader = (): JSX.Element => {
  const css = { borderRadius: '1rem' };
  return (
    <Grid xsmall={1} medium={2} large={3}>
      <RectangleSkeleton width="100%" height="25rem" style={css} />
      <RectangleSkeleton width="100%" height="25rem" style={css} />
      <RectangleSkeleton width="100%" height="25rem" style={css} />
    </Grid>
  );
};

const ITEMS_PER_PAGE = 12;

const SearchEvents: FC<ISearchEventsProps> = ({ inputTerm }) => {
  const router = useRouter();
  const page = parseInt(router.query.page as string) || 1;

  const {
    data: events,
    isLoading,
    hasError,
    error
  } = useEvents(inputTerm, page - 1, ITEMS_PER_PAGE);

  // TODO https://morressier.atlassian.net/browse/DISCO-682
  // if (!isLoading) {
  //   logEvent('SEARCH_RESULTS_LOADED', {
  //     inputTerm,
  //     contentType: 'EVENT'
  //   });
  // }

  useEffect(() => {
    const { scrollTo } = router.query;
    if (events && scrollTo && events.total !== 0) {
      const element = document?.querySelector(`#${scrollTo}`);
      element?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [events]);

  if (!isLoading && events?.total === 0) {
    return (
      <Container>
        <MetaTags title={`No Search result for ${inputTerm}`} />
        <HomeSearchNoResults key="events-no-results" searchTerm={inputTerm} />
      </Container>
    );
  }

  const gotoEvent = async (eventSearchResultId: string, index: number) => {
    logEvent('SEARCH_RESULTS_RESULT_CLICKED', {
      resultLocation: index,
      page,
      searchTab: 'EVENTS',
      typeOfCard: 'EVENT',
      ID: eventSearchResultId
    });
  };

  const renderEventCard = () => {
    return events?.data?.map((eventSearchResult, index) => {
      const startDate = eventSearchResult.startDate * 1000;
      const endDate = eventSearchResult.endDate * 1000;

      return (
        <CardLink
          id={`event${eventSearchResult.id}`}
          key={eventSearchResult.id}
          className="test-EventCard"
          href={`${origin}/o/event/${eventSearchResult.id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => gotoEvent(eventSearchResult.id, index + 1)}
        >
          <EventCard
            {...eventSearchResult}
            startDate={startDate}
            endDate={endDate}
          />
        </CardLink>
      );
    });
  };

  if (hasError && !isLoading)
    return (
      <Container>
        <div>Error searching for events: {error?.message}</div>
      </Container>
    );

  // Page meta data
  const pageTitle = `Search Events${inputTerm && ' - '}${inputTerm}${
    page && ' - '
  }${page}`;
  const paginated = {
    router,
    lastPage: Math.ceil((events?.total || 0) / ITEMS_PER_PAGE)
  };

  return (
    <Container>
      <MetaTags title={pageTitle} paginated={paginated} />
      <ResultsText
        totalResults={events?.total?.toLocaleString() || ''}
        type="Events"
        searchTerm={inputTerm}
      />

      {isLoading ? (
        <SkeletonLoader />
      ) : (
        <Grid xsmall={1} medium={2} large={3}>
          {renderEventCard()}
        </Grid>
      )}

      <PagingContainer key="events-paging-container">
        <Paging
          key="events-paging"
          currentPage={page}
          totalPages={Math.ceil((events?.total || 0) / ITEMS_PER_PAGE)}
          previousButtonText="Prev"
          nextButtonText="Next"
          renderLink={pagingLink('/search/events', inputTerm, page)}
        />
      </PagingContainer>
    </Container>
  );
};

export default SearchEvents;
